import React from 'react';
import { Helmet } from 'react-helmet-async';

const SITE_NAME = 'OneAI Humanize';
const SITE_TAGLINE = 'Human-first AI tools for clearer, natural writing.';
const DEFAULT_OG_IMAGE = (typeof window !== 'undefined') ? (window.location.origin + '/logo512.png') : '/logo512.png';

function ensureTrailingSlashInUrl(raw) {
  if (!raw) return raw;
  try {
    const url = new URL(raw, typeof window !== 'undefined' ? window.location.origin : 'https://oneaihuman.com');
    const last = url.pathname.split('/').filter(Boolean).slice(-1)[0] || '';
    const hasFileLike = last && last.includes('.');
    if (!url.pathname.endsWith('/') && !hasFileLike) url.pathname = url.pathname + '/';
    return url.toString();
  } catch (e) {
    let s = String(raw || '').trim();
    if (!s.startsWith('/')) s = '/' + s;
    if (!s.endsWith('/')) s = s + '/';
    return s;
  }
}

function tidyTitle(t) {
  if (!t) return SITE_NAME;
  const s = String(t).trim();
  if (s.length <= 60) return s;
  return s.substring(0, 57).trim() + '...';
}

function tidyDescription(d, title) {
  const base = (d || '').trim();
  if (!base) {
    const gen = `${title || SITE_NAME} — ${SITE_TAGLINE}`;
    return gen.length > 160 ? gen.substring(0, 157).trim() + '...' : gen;
  }
  if (base.length < 150) {
    const combined = (base + ' ' + SITE_TAGLINE).trim();
    return combined.length > 160 ? combined.substring(0, 157).trim() + '...' : combined;
  }
  if (base.length > 160) return base.substring(0, 157).trim() + '...';
  return base;
}

export default function PageHelmet({ title, description, canonical, image, type = 'website' }) {
  const finalTitle = tidyTitle(title || SITE_NAME);
  const finalDescription = tidyDescription(description, title);
  const rawCanonical = canonical || (typeof window !== 'undefined' ? window.location.href : undefined);
  const canonicalUrl = rawCanonical ? ensureTrailingSlashInUrl(rawCanonical) : undefined;
  const finalImage = image || DEFAULT_OG_IMAGE;

  return (
    <Helmet>
      {/* Primary title & description */}
      <title>{finalTitle}</title>
      <meta name="description" content={finalDescription} />

      {/* Canonical */}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}

      {/* Open Graph */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDescription} />
      <meta property="og:image" content={finalImage} />
      <meta property="og:type" content={type} />
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDescription} />
      <meta name="twitter:image" content={finalImage} />
    </Helmet>
  );
}
